import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import { api } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { formatDate, formatDateTime, formatEnrollmentDeadline } from '../utils/dateUtils';
import ReactMarkdown from 'react-markdown';
import {
  Box, Container, Card, CardContent, Button, Typography, CircularProgress,
  Alert, Chip, Divider, Paper, Stack
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Schedule as ScheduleIcon,
  AttachMoney as MoneyIcon,
  School as SchoolIcon,
  LocationOn as LocationIcon,
  Event as EventIcon,
  Male as MaleIcon,
  Female as FemaleIcon
} from '@mui/icons-material';

const volumePrice = (base, discount, minimum, position) => {
  let price = base;
  if (discount && discount > 0) price = base - discount * (position - 1);
  if (minimum !== null && minimum !== undefined) price = Math.max(price, minimum);
  return price;
};

function ProgramDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [program, setProgram] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchProgramDetails();
    fetchSessions();
  }, [id]);

  const fetchProgramDetails = async () => {
    try {
      setLoading(true);
      setError('');
      try {
        setProgram(await api.getProgram(id));
      } catch (authErr) {
        if (authErr.response?.status === 401) setProgram(await api.getPublicProgram(id));
        else throw authErr;
      }
    } catch (err) {
      console.error('Error fetching program details:', err);
      setError('Failed to load program details. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const fetchSessions = async () => {
    try { setSessions((await api.getSessions(id)) || []); } catch { /* optional */ }
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'background.default' }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={60} />
          <Typography variant="h6" sx={{ mt: 2 }}>Loading program details...</Typography>
        </Box>
      </Box>
    );
  }

  if (error || !program) {
    return (
      <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', pt: 12, pb: 4 }}>
        <Container maxWidth="lg">
          <Alert severity="error" sx={{ mb: 3 }}>{error || 'Program not found.'}</Alert>
          <Button component={RouterLink} to="/programs" variant="contained" startIcon={<ArrowBackIcon />}>
            Back to Programs
          </Button>
        </Container>
      </Box>
    );
  }

  const deadlinePassed = program.enrollment_deadline && new Date(program.enrollment_deadline) < new Date();
  const canEnroll = program.enrollment_open && !deadlinePassed;

  const enrollLabel = !isAuthenticated
    ? 'Sign In to Enroll'
    : !program.enrollment_open ? 'Enrollment Closed'
    : deadlinePassed ? 'Deadline Passed'
    : 'Enroll Now';

  const handleEnroll = () => {
    if (!isAuthenticated) { navigate('/login', { state: { from: `/programs/${id}` } }); return; }
    navigate(`/programs/${id}/enroll`);
  };

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', pt: 12, pb: 4 }}>
      <Container maxWidth="lg">
        <Button component={RouterLink} to="/programs" startIcon={<ArrowBackIcon />} sx={{ mb: 3 }}>
          Back to Programs
        </Button>

        <Card sx={{ mb: 4 }}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Typography variant="h3" component="h1" sx={{ fontWeight: 700, flex: 1 }}>
                {program.name}
              </Typography>
            </Box>

            {!program.enrollment_open && (
              <Alert severity="info" sx={{ mb: 3 }}>
                <strong>Enrollment for this program has closed.</strong> You can still view details.
              </Alert>
            )}

            <Divider sx={{ mb: 3 }} />

            {/* Pricing */}
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>Pricing</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
              {program.feature_per_role_pricing && program.price_map ? (
                Object.entries(program.price_map).map(([role, price]) => (
                  <Chip key={role} icon={<MoneyIcon />}
                    label={`${role.charAt(0).toUpperCase() + role.slice(1)}: $${Number(price).toFixed(2)}`}
                    color="primary" sx={{ fontSize: '0.95rem', height: 36, px: 1 }} />
                ))
              ) : program.feature_volume_discount ? (
                [1, 2, 3, 4].map((pos) => {
                  const price = volumePrice(program.price, program.volume_discount, program.minimum_price, pos);
                  const label = pos === 1 ? '1st Child' : pos === 2 ? '2nd Child' : pos === 3 ? '3rd Child' : '4th+ Child';
                  return <Chip key={pos} icon={<MoneyIcon />} label={`${label}: $${price.toFixed(2)}`}
                    color="primary" sx={{ fontSize: '0.95rem', height: 36, px: 1 }} />;
                })
              ) : (
                <Chip icon={<MoneyIcon />} label={`$${Number(program.price || 0).toFixed(2)}`}
                  color="primary" sx={{ fontSize: '0.95rem', height: 36, px: 1 }} />
              )}
            </Box>
            <Divider sx={{ mb: 3 }} />

            {/* Description */}
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>Description</Typography>
            {program.feature_markdown ? (
              <Box sx={{ mb: 4, '& p': { mb: 1 }, '& ul, & ol': { pl: 2 }, '& li': { mb: 0.5 } }}>
                <ReactMarkdown>{program.description || ''}</ReactMarkdown>
              </Box>
            ) : (
              <Typography variant="body1" sx={{ mb: 4, lineHeight: 1.8, whiteSpace: 'pre-line' }}>
                {program.description}
              </Typography>
            )}

            {/* Curriculum (term programs) */}
            {program.curriculum && (
              <>
                <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>Curriculum</Typography>
                <Typography variant="body1" sx={{ mb: 4, lineHeight: 1.8 }}>{program.curriculum}</Typography>
              </>
            )}

            {/* Rules */}
            {program.rules && (
              <>
                <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>Program Rules</Typography>
                <Box sx={{ mb: 4, '& ol': { pl: 3 }, '& li': { mb: 1.5, lineHeight: 1.8 } }}>
                  <ReactMarkdown>{program.rules}</ReactMarkdown>
                </Box>
              </>
            )}

            {/* Details grid */}
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>Program Details</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 3, mb: 2 }}>
              {/* Dates (derived from sessions) */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ScheduleIcon sx={{ color: 'primary.main' }} />
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block">Program Dates</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {program.start_date && program.end_date
                      ? `${formatDate(program.start_date)} - ${formatDate(program.end_date)}`
                      : 'N/A'}
                  </Typography>
                </Box>
              </Box>

              {program.enrollment_deadline && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <EventIcon sx={{ color: 'warning.main' }} />
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">Enrollment Deadline</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>{formatEnrollmentDeadline(program.enrollment_deadline)}</Typography>
                  </Box>
                </Box>
              )}

              {program.location && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LocationIcon sx={{ color: 'primary.main' }} />
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">Location</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>{program.location}</Typography>
                  </Box>
                </Box>
              )}

              {(program.min_grade || program.max_grade) && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <SchoolIcon sx={{ color: 'primary.main' }} />
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">Grade Range</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {program.min_grade && program.max_grade ? `Grades ${program.min_grade}-${program.max_grade}`
                        : program.min_grade ? `Grade ${program.min_grade}+` : `Up to Grade ${program.max_grade}`}
                    </Typography>
                  </Box>
                </Box>
              )}

              {program.male_capacity > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <MaleIcon sx={{ color: 'primary.main' }} />
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">Male Capacity</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>{program.male_capacity} students</Typography>
                  </Box>
                </Box>
              )}
              {program.female_capacity > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FemaleIcon sx={{ color: 'primary.main' }} />
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">Female Capacity</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>{program.female_capacity} students</Typography>
                  </Box>
                </Box>
              )}
            </Box>

            <Divider sx={{ my: 3 }} />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button variant="contained" size="large" onClick={handleEnroll} disabled={isAuthenticated && !canEnroll}>
                {enrollLabel}
              </Button>
            </Box>
          </CardContent>
        </Card>

        {/* Sessions (term programs) */}
        {sessions.length > 0 && (
          <Card sx={{ mb: 4 }}>
            <CardContent sx={{ p: 4 }}>
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 3 }}>Program Sessions</Typography>
              <Divider sx={{ mb: 3 }} />
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {sessions.slice().sort((a, b) => new Date(a.start_time) - new Date(b.start_time)).map((session) => (
                  <Paper key={session.id} variant="outlined" sx={{ p: 3 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>{session.name}</Typography>
                    {session.description && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{session.description}</Typography>
                    )}
                    <Stack direction="row" flexWrap="wrap" gap={2} alignItems="center">
                      <Chip icon={<ScheduleIcon />} label={formatDateTime(session.start_time)} size="small" variant="outlined" />
                      <Typography variant="body2" color="text.secondary">to</Typography>
                      <Chip icon={<ScheduleIcon />} label={formatDateTime(session.end_time)} size="small" variant="outlined" />
                      {session.location && <Chip icon={<LocationIcon />} label={session.location} size="small" color="primary" variant="outlined" />}
                    </Stack>
                  </Paper>
                ))}
              </Box>
            </CardContent>
          </Card>
        )}
      </Container>
    </Box>
  );
}

export default ProgramDetails;
